import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/lib/property";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ClockCounterClockwiseIcon, FunnelIcon, DownloadSimpleIcon, EyeIcon,
} from "@phosphor-icons/react";

const ENTITY_META = {
  reservation: { label: "Reservation", tone: "bg-secondary/15 text-secondary" },
  invoice: { label: "Invoice", tone: "bg-primary/15 text-primary" },
  expense: { label: "Expense", tone: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]" },
  staff: { label: "Staff", tone: "bg-muted text-muted-foreground" },
  property: { label: "Property", tone: "bg-primary/10 text-primary" },
  room: { label: "Room", tone: "bg-secondary/10 text-secondary" },
  pos_menu: { label: "POS · Menu", tone: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]" },
  pos_order: { label: "POS · Order", tone: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]" },
  platform_settings: { label: "Platform Settings", tone: "bg-muted text-muted-foreground" },
  master: { label: "Master", tone: "bg-gold/20 text-gold" },
};

const ACTION_TONE = {
  created: "text-[hsl(var(--success))]",
  updated: "text-primary",
  deleted: "text-destructive",
  approved: "text-[hsl(var(--success))]",
  rejected: "text-destructive",
  paid: "text-[hsl(var(--success))]",
  generated: "text-primary",
  bulk_create: "text-[hsl(var(--success))]",
  stock_adjust: "text-secondary",
  master_transfer: "text-gold",
};

export default function Audit() {
  const { activeId, properties } = useProperty();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({
    entity_type: "all",
    action: "all",
    actor_email: "",
    property_scope: "active",
    start: "",
    end: "",
    summary: "",
  });

  const scopedPropertyId = useMemo(() => {
    if (filters.property_scope === "all") return null;
    if (filters.property_scope === "active") return activeId;
    return filters.property_scope;
  }, [filters.property_scope, activeId]);

  const load = () => {
    setLoading(true);
    const params = { limit: 500 };
    if (filters.entity_type !== "all") params.entity_type = filters.entity_type;
    if (filters.action !== "all") params.action = filters.action;
    if (filters.actor_email) params.actor_email = filters.actor_email.trim();
    if (scopedPropertyId) params.property_id = scopedPropertyId;
    if (filters.start) params.start = filters.start;
    if (filters.end) params.end = filters.end;
    if (filters.summary) params.summary = filters.summary.trim();
    api.get("/audit", { params })
      .then((r) => setRows(r.data))
      .catch(() => toast.error("Failed to load audit log"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.entity_type, filters.action, scopedPropertyId, filters.start, filters.end, filters.summary]);

  const exportCsv = async () => {
    const params = new URLSearchParams();
    if (filters.entity_type !== "all") params.set("entity_type", filters.entity_type);
    if (filters.actor_email) params.set("actor_email", filters.actor_email);
    if (scopedPropertyId) params.set("property_id", scopedPropertyId);
    if (filters.start) params.set("start", filters.start);
    if (filters.end) params.set("end", filters.end);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/audit/export?${params.toString()}`;
    const token = localStorage.getItem("token");
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return toast.error("Export failed");
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const propertyName = (id) => properties.find((p) => p.id === id)?.name || (id ? "—" : "Global");

  return (
    <div>
      <PageHeader
        overline="Governance"
        title="Audit history"
        description="Immutable log of every mutation across bookings, invoices, expenses, staff, POS, master transfers, and platform settings. Retained up to 10,000 rows per property."
        actions={<Button variant="outline" onClick={exportCsv} data-testid="audit-export-btn"><DownloadSimpleIcon size={14} className="mr-1" /> Export CSV</Button>}
      />

      {/* Filter bar */}
      <Card className="p-4 mb-4" data-testid="audit-filter-bar">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <FunnelIcon size={16} weight="duotone" />
            <span className="label-overline">Filters</span>
          </div>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-6 gap-3 min-w-[240px]">
            <div>
              <Label className="text-xs">Entity</Label>
              <Select value={filters.entity_type} onValueChange={(v) => setFilters({ ...filters, entity_type: v })}>
                <SelectTrigger className="mt-1" data-testid="audit-entity-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All entities</SelectItem>
                  {Object.entries(ENTITY_META).map(([k, m]) => (
                    <SelectItem key={k} value={k}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Action</Label>
              <Select value={filters.action} onValueChange={(v) => setFilters({ ...filters, action: v })}>
                <SelectTrigger className="mt-1" data-testid="audit-action-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All actions</SelectItem>
                  <SelectItem value="created">Created</SelectItem>
                  <SelectItem value="updated">Updated</SelectItem>
                  <SelectItem value="deleted">Deleted</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="generated">Generated</SelectItem>
                  <SelectItem value="bulk_create">Bulk create</SelectItem>
                  <SelectItem value="stock_adjust">Stock adjust</SelectItem>
                  <SelectItem value="master_transfer">Master transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Property</Label>
              <Select value={filters.property_scope} onValueChange={(v) => setFilters({ ...filters, property_scope: v })}>
                <SelectTrigger className="mt-1" data-testid="audit-property-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active property</SelectItem>
                  <SelectItem value="all">All properties</SelectItem>
                  {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">From</Label>
              <Input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} className="mt-1" data-testid="audit-start" />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} className="mt-1" data-testid="audit-end" />
            </div>
            <div>
              <Label className="text-xs">Actor email</Label>
              <Input placeholder="e.g. admin@…" value={filters.actor_email} onChange={(e) => setFilters({ ...filters, actor_email: e.target.value })} onBlur={load} className="mt-1" data-testid="audit-actor" />
            </div>
          </div>
        </div>
        <div className="mt-3">
          <Label className="text-xs">Search summary (guest name, invoice #, amount…)</Label>
          <Input
            placeholder="Type to filter across meta values…"
            value={filters.summary}
            onChange={(e) => setFilters({ ...filters, summary: e.target.value })}
            className="mt-1"
            data-testid="audit-summary-search"
          />
        </div>
      </Card>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-40">When</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Entity</TableHead>
              <TableHead>Property</TableHead>
              <TableHead>Summary</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={7} className="text-center py-6 text-muted-foreground italic">Loading…</TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground"><ClockCounterClockwiseIcon size={28} className="mx-auto mb-2 opacity-40" />No audit entries match the filters.</TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`audit-row-${r.id}`}>
                <TableCell className="text-xs font-mono-plex">{r.created_at?.slice(0, 19).replace("T", " ")}</TableCell>
                <TableCell>
                  <div className="text-sm">{r.actor_email || <span className="italic text-muted-foreground">system</span>}</div>
                  {r.actor_role && <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{r.actor_role}</div>}
                </TableCell>
                <TableCell className={`text-sm font-medium capitalize ${ACTION_TONE[r.action] || ""}`}>{r.action?.replace("_", " ")}</TableCell>
                <TableCell><Badge variant="outline" className={ENTITY_META[r.entity_type]?.tone || ""}>{ENTITY_META[r.entity_type]?.label || r.entity_type}</Badge></TableCell>
                <TableCell className="text-xs text-muted-foreground">{propertyName(r.property_id)}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-md truncate">
                  {r.meta && Object.entries(r.meta).slice(0, 3).map(([k, v]) => (
                    <span key={k} className="mr-2"><span className="text-muted-foreground/80">{k}:</span> <span className="text-foreground">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span></span>
                  ))}
                </TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => setSelected(r)} title="View" data-testid={`audit-view-${r.id}`}><EyeIcon size={14} /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {selected && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
        >
          <Card className="max-w-2xl w-full max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="label-overline">Audit entry</p>
                  <h3 className="font-display text-xl mt-1 capitalize">{selected.action?.replace("_", " ")} · {ENTITY_META[selected.entity_type]?.label}</h3>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setSelected(null)}>Close</Button>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Timestamp:</span> <span className="font-mono-plex">{selected.created_at}</span></div>
                <div><span className="text-muted-foreground">Actor:</span> {selected.actor_email} <span className="text-xs uppercase text-muted-foreground">({selected.actor_role})</span></div>
                <div><span className="text-muted-foreground">Entity ID:</span> <span className="font-mono-plex text-xs">{selected.entity_id}</span></div>
                <div><span className="text-muted-foreground">Property:</span> {propertyName(selected.property_id)}</div>
              </div>
              {selected.meta && Object.keys(selected.meta).length > 0 && (
                <div className="mt-4">
                  <p className="label-overline mb-2">Details</p>
                  <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-40" data-testid="audit-meta-json">{JSON.stringify(selected.meta, null, 2)}</pre>
                </div>
              )}
              {selected.before && (
                <div className="mt-4">
                  <p className="label-overline mb-2">Before</p>
                  <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-40">{JSON.stringify(selected.before, null, 2)}</pre>
                </div>
              )}
              {selected.after && (
                <div className="mt-4">
                  <p className="label-overline mb-2">After</p>
                  <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-40">{JSON.stringify(selected.after, null, 2)}</pre>
                </div>
              )}
              {selected.action === "deleted" && selected.entity_type === "invoice" && selected.before && (
                <div className="mt-6 rounded-md border-2 border-destructive/30 bg-destructive/5 p-4" data-testid="audit-deleted-invoice-view">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="label-overline text-destructive">Deleted invoice · reconstructed</p>
                      <h4 className="font-display text-xl mt-1">{selected.before.number}</h4>
                    </div>
                    <span className="text-[10px] text-muted-foreground">Deleted at {selected.created_at?.slice(0, 19).replace("T", " ")} by {selected.actor_email}</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Guest</p>
                      <p className="font-medium">{selected.before.guest_snapshot?.name || "—"}</p>
                      {selected.before.guest_snapshot?.phone && <p className="text-[10px] text-muted-foreground">{selected.before.guest_snapshot.phone}</p>}
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Room</p>
                      <p className="font-medium">{selected.before.room_snapshot?.number ? `#${selected.before.room_snapshot.number}` : "—"}</p>
                      {selected.before.room_snapshot?.room_type && <p className="text-[10px] text-muted-foreground">{selected.before.room_snapshot.room_type}</p>}
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Stay</p>
                      <p className="text-xs font-mono-plex">{selected.before.check_in || "?"} → {selected.before.check_out || "?"}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Total</p>
                      <p className="font-display text-lg text-primary">₹{Number(selected.before.total || 0).toLocaleString("en-IN")}</p>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs border-t border-destructive/20 pt-3">
                    <div><span className="text-muted-foreground">Room charges:</span> ₹{Number(selected.before.room_charges || 0).toLocaleString("en-IN")}</div>
                    <div><span className="text-muted-foreground">GST ({selected.before.tax_rate}% {selected.before.tax_inclusive ? "incl." : "excl."}):</span> ₹{Number(selected.before.tax_amount || 0).toLocaleString("en-IN")}</div>
                    <div><span className="text-muted-foreground">Extras:</span> ₹{Number(selected.before.extra_charges || 0).toLocaleString("en-IN")}</div>
                    <div><span className="text-muted-foreground">Discount:</span> ₹{Number(selected.before.discount || 0).toLocaleString("en-IN")}</div>
                    <div><span className="text-muted-foreground">Deposit applied:</span> ₹{Number(selected.before.deposit_applied || 0).toLocaleString("en-IN")}</div>
                    <div><span className="text-muted-foreground">Paid:</span> ₹{Number(selected.before.paid || 0).toLocaleString("en-IN")}</div>
                    <div><span className="text-muted-foreground">Status:</span> {selected.before.status}</div>
                  </div>
                  {(selected.before.line_items || []).length > 0 && (
                    <div className="mt-4">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Line items</p>
                      <table className="w-full text-xs">
                        <tbody>
                          {selected.before.line_items.map((li, idx) => (
                            <tr key={idx} className="border-t border-destructive/10">
                              <td className="py-1">{li.description || li.name}</td>
                              <td className="py-1 text-right">{li.qty}×</td>
                              <td className="py-1 text-right font-mono-plex">₹{Number(li.amount || li.rate || 0).toLocaleString("en-IN")}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <p className="text-[10px] italic text-muted-foreground mt-3">
                    This is a read-only reconstruction from the audit snapshot. To restore, ask an admin to regenerate the invoice against the original reservation.
                  </p>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
